// GoogleMapsLoader usando el método recomendado por Google: Dynamic Library Import
// Referencia: https://developers.google.com/maps/documentation/javascript/load-maps-js-api

export class GoogleMapsLoader {
  private static instance: GoogleMapsLoader
  private static loadPromise: Promise<void> | null = null
  private static isLoaded = false

  public static getInstance(): GoogleMapsLoader {
    if (!GoogleMapsLoader.instance) {
      GoogleMapsLoader.instance = new GoogleMapsLoader()
    }
    return GoogleMapsLoader.instance
  }

  public async load(): Promise<void> {
    // Si ya está cargado, resolver inmediatamente
    if (GoogleMapsLoader.isLoaded) {
      return Promise.resolve()
    }

    // Si hay un proceso de carga en curso, reutilizar la promesa
    if (GoogleMapsLoader.loadPromise) {
      return GoogleMapsLoader.loadPromise
    }

    // Crear nueva promesa de carga
    GoogleMapsLoader.loadPromise = this.loadGoogleMaps()
    return GoogleMapsLoader.loadPromise
  }

  private async loadGoogleMaps(): Promise<void> {
    try {
      // Primer paso: Cargar el script bootstrap si aún no existe
      await this.ensureBootstrapLoaded()

      // Segundo paso: Usar importLibrary (Dynamic Library Import - método recomendado)
      // Esto carga las librerías bajo demanda
      const [mapsLibrary, markerLibrary] = await Promise.all([
        (window as any).google?.maps?.importLibrary?.('maps') || Promise.reject(new Error('Maps library not available')),
        (window as any).google?.maps?.importLibrary?.('marker') || Promise.reject(new Error('Marker library not available'))
      ])

      // Verificar que todo esté disponible
      if (!this.isFullyInitialized()) {
        throw new Error('Google Maps libraries not properly initialized')
      }

      GoogleMapsLoader.isLoaded = true
      GoogleMapsLoader.loadPromise = null
    } catch (error) {
      GoogleMapsLoader.loadPromise = null
      throw new Error(`Failed to load Google Maps: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  private async ensureBootstrapLoaded(): Promise<void> {
    // Si ya existe el bootstrap, no cargar de nuevo
    if ((window as any).google?.maps?.importLibrary) {
      return Promise.resolve()
    }

    return new Promise((resolve, reject) => {
      try {
        // Crear el script bootstrap con loading=async (recomendado por Google)
        const script = document.createElement('script')
        script.async = true
        script.defer = true
        script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&loading=async`
        script.type = 'text/javascript'

        // Resolver cuando se cargue
        script.onload = () => {
          // Esperar a que google.maps esté completamente listo
          const checkReady = setInterval(() => {
            if ((window as any).google?.maps?.importLibrary) {
              clearInterval(checkReady)
              resolve()
            }
          }, 50)

          // Timeout de seguridad
          setTimeout(() => {
            clearInterval(checkReady)
            if ((window as any).google?.maps?.importLibrary) {
              resolve()
            } else {
              reject(new Error('Bootstrap loaded but importLibrary not available'))
            }
          }, 3000)
        }

        script.onerror = () => {
          reject(new Error('Failed to load Google Maps bootstrap script'))
        }

        document.head.appendChild(script)
      } catch (error) {
        reject(error)
      }
    })
  }

  private isFullyInitialized(): boolean {
    return !!(
      window.google &&
      window.google.maps &&
      window.google.maps.Map &&
      window.google.maps.Marker &&
      window.google.maps.InfoWindow &&
      window.google.maps.event &&
      // Verificar que los constructores funcionen
      typeof window.google.maps.Map === 'function' &&
      typeof window.google.maps.Marker === 'function' &&
      typeof window.google.maps.InfoWindow === 'function'
    )
  }

  public isGoogleMapsLoaded(): boolean {
    return GoogleMapsLoader.isLoaded && this.isFullyInitialized()
  }

  // Método para debug
  public getStatus(): string {
    if (this.isFullyInitialized()) return 'fully-loaded'
    if (window.google?.maps?.importLibrary) return 'bootstrap-loaded'
    if (window.google?.maps) return 'partially-loaded'
    if (GoogleMapsLoader.loadPromise) return 'loading'
    return 'not-loaded'
  }

  // Debug: ver estado completo
  public getDetailedStatus(): any {
    return {
      status: this.getStatus(),
      isLoaded: GoogleMapsLoader.isLoaded,
      hasPromise: GoogleMapsLoader.loadPromise !== null,
      hasGoogle: !!window.google,
      hasMaps: !!window.google?.maps,
      hasBootstrapMethod: !!(window as any).google?.maps?.importLibrary,
      hasMap: !!window.google?.maps?.Map,
      hasMarker: !!window.google?.maps?.Marker,
      hasAdvancedMarker: !!window.google?.maps?.marker?.AdvancedMarkerElement,
      apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY ? '✓ Configured' : '✗ Missing',
      method: 'Dynamic Library Import (Official Google Method)',
      timestamp: new Date().toISOString()
    }
  }
}
